/**
 * A 1x1 depth texture with a compare sampler, bound to the splat shadow-map slots (the two
 * directional maps and the spot / point atlas) whenever no light fills them. The relighting
 * shader declares the slots unconditionally (outside the shadow pass), and both backends reject
 * a draw whose shadow sampler has nothing compatible bound (WebGL2: sampler / texture mismatch;
 * WebGPU: bind-group layout mismatch). It is never sampled: an unused slot has strength 0 and
 * the shader returns before the lookup. The cloud-shadow slots get a 1x1 colour texture.
 */
import * as pc from 'playcanvas';
import { SPLAT_SHADOW_UNIFORMS } from './splatShadowShader';
import { CLOUD_SHADOW_UNIFORMS } from '../clouds/cloudShaders';
import { getCloudShadowPlaceholder } from '../clouds/cloudShadowPlaceholder';

const placeholders = new WeakMap<pc.GraphicsDevice, pc.Texture>();

export function getShadowMapPlaceholder(device: pc.GraphicsDevice): pc.Texture {
    let texture = placeholders.get(device);
    if (!texture) {
        texture = new pc.Texture(device, {
            name: 'SplatShadowPlaceholder',
            format: pc.PIXELFORMAT_DEPTH,
            width: 1,
            height: 1,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        texture.compareOnRead = true;
        texture.compareFunc = pc.FUNC_LESS;
        placeholders.set(device, texture);
    }
    return texture;
}

/**
 * Bind the placeholder to any shadow-map slot of `material` that has nothing yet. Returns true
 * when it changed something (the caller then calls material.update()).
 */
export function ensureShadowMapBindings(material: pc.Material, device: pc.GraphicsDevice): boolean {
    let changed = false;
    for (const name of [...SPLAT_SHADOW_UNIFORMS.maps, SPLAT_SHADOW_UNIFORMS.atlas]) {
        if (!material.getParameter(name)) {
            material.setParameter(name, getShadowMapPlaceholder(device));
            changed = true;
        }
    }
    // cloud-shadow maps are plain (non-depth) textures
    for (const name of CLOUD_SHADOW_UNIFORMS.maps) {
        if (!material.getParameter(name)) {
            material.setParameter(name, getCloudShadowPlaceholder(device));
            changed = true;
        }
    }
    return changed;
}
